import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { DoctorWorkingHour } from '@/types/settings-api';

/**
 * Canonical React Query key for fetching a single working-hour record
 * by id. Mutations in the follow-up PR can invalidate this slot
 * directly. Same factory convention as other settings query keys.
 */
export function doctorWorkingHourByIdQueryKey(
  workingHourId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'doctor-working-hour-by-id', workingHourId];
}

/**
 * Fetch a single doctor working-hour record by id — PROD-762 PR-B
 * (foundation for the upcoming editor dialog).
 *
 * GET /api/Doctor/WorkingHours/{workingHourId}    (PLURAL — easy trap)
 *
 * Returns the canonical edit shape (`DoctorWorkingHour`), distinct
 * from the list endpoint's `DoctorWorkingHoursListItem` envelope.
 *
 * **Path quirk**: GET uses `/Doctor/WorkingHours/{id}` plural while
 * PUT uses `/Doctor/WorkingHour/{id}` singular. Both verified against
 * live dev cURLs 2026-05-15. The plural form was a 405 vs the
 * singular GET path I tried first — easy to lose an hour to this
 * if you're not warned. The singular vs plural discrimination is
 * load-bearing on this surface.
 *
 * The editor (separate PR) fetches via this hook when the user clicks
 * Edit on a list row. The hook is independent of the list cache so
 * a stale list doesn't drag a stale editor with it.
 */
export function useDoctorWorkingHourById(
  workingHourId: string | null | undefined,
) {
  return useQuery({
    queryKey: doctorWorkingHourByIdQueryKey(workingHourId),
    queryFn: ({ signal }) => {
      if (!workingHourId) {
        throw new Error(
          'useDoctorWorkingHourById called without workingHourId',
        );
      }
      return get<DoctorWorkingHour>(
        `/api/Doctor/WorkingHours/${workingHourId}`,
        { signal },
      );
    },
    enabled: !!workingHourId,
    // Long stale time — the canonical record is small and the editor
    // dialog should reflect the user's edits-in-progress, not refetch.
    staleTime: 60_000,
  });
}
